import { useEffect, useState } from 'react';

const SCRIPT_ID = 'google-maps-places-script';
let scriptPromise = null;

const isPlacesReady = () => Boolean(window.google?.maps?.places?.AutocompleteService);

/**
 * Loads the Google Maps JS API (with the ``places`` library) exactly once
 * per page. Subsequent callers receive the same in-flight promise so the
 * `<script>` is never duplicated.
 *
 * After the script tag fires ``onload`` we still poll for
 * ``window.google.maps.places.AutocompleteService`` because the
 * ``loading=async`` bootstrap loader exposes the Maps core API first and
 * imports the Places library a tick later. Polling cleanly handles either
 * loading mode (sync or async).
 */
export function useGoogleMapsScript() {
  const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
  const [ready, setReady] = useState(isPlacesReady);
  const [error, setError] = useState(() => (apiKey ? null : 'Missing REACT_APP_GOOGLE_MAPS_API_KEY'));

  useEffect(() => {
    if (!apiKey || ready) return;

    if (!scriptPromise) {
      scriptPromise = new Promise((resolve, reject) => {
        const waitForPlaces = (deadline) => {
          if (isPlacesReady()) return resolve();
          if (Date.now() > deadline) return reject(new Error('Places library timed out (10s)'));
          setTimeout(() => waitForPlaces(deadline), 80);
        };
        const onScriptLoaded = () => {
          // Try the modern importLibrary path first; if it errors or the
          // namespace still isn't populated, fall back to polling.
          const tryImport = async () => {
            try {
              if (window.google?.maps?.importLibrary && !isPlacesReady()) {
                await window.google.maps.importLibrary('places');
              }
            } catch { /* fall through to polling */ }
            waitForPlaces(Date.now() + 10_000);
          };
          tryImport();
        };
        const existing = document.getElementById(SCRIPT_ID);
        if (existing) {
          if (window.google?.maps) {
            onScriptLoaded();
          } else {
            existing.addEventListener('load', onScriptLoaded);
            existing.addEventListener('error', () => reject(new Error('Google Maps script failed to load')));
          }
          return;
        }
        const s = document.createElement('script');
        s.id = SCRIPT_ID;
        s.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(apiKey)}&libraries=places&v=weekly`;
        s.async = true;
        s.defer = true;
        s.onload = onScriptLoaded;
        s.onerror = () => reject(new Error('Google Maps script failed to load'));
        document.head.appendChild(s);
      });
    }

    let cancelled = false;
    scriptPromise.then(() => {
      if (!cancelled) setReady(true);
    }).catch(err => {
      if (!cancelled) setError(err.message || 'Failed to load Google Maps');
      scriptPromise = null;  // allow retry on next mount
    });
    return () => { cancelled = true; };
  }, [apiKey, ready]);

  return { ready, error };
}
